import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Divider,
  useTheme,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { tokens } from "../../theme";
import { useParams } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import * as image from "../../assets";
import useStudentEvaluationService from "../../api/services/StudentEvaluationService";
import useRectorCommentService from "../../api/services/RectorCommentService";
import useAccountantService from "../../api/services/AccountantService";
import PrintIcon from "@mui/icons-material/Print";
import generateStudentEvaluationReport from "../../utils/StudentEvaluationReport";

const StudentEvaluationReport = () => {
  const { studentId, termId } = useParams();
  const theme = useTheme();
  const borderColor = theme.palette.mode === "dark" ? "white" : "black";
  const colors = tokens(theme.palette.mode);

  const [report, setReport] = useState(null);
  const [rectorComment, setRectorComment] = useState(null);
  const [accountantData, setAccountantData] = useState(null);
  const [loading, setLoading] = useState(true);

  const { getEvaluationByTermIdAndStudentId } = useStudentEvaluationService();
  const { getRectorCommentByTermStudent } = useRectorCommentService();
  const { getAccountantByTermStudent } = useAccountantService();

  const stripHtmlTags = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  };

  const getSavedRectorComments = () =>
    stripHtmlTags(rectorComment?.rectorComments || report?.rectorComments || "");

  const buildReportForPdf = () => {
    if (!report) return null;
    return {
      ...report,
      rectorComments: getSavedRectorComments(),
    };
  };

  const formatRectorComments = (comments) => {
    if (!comments) return null;

    const lines = comments.split('\n');

    return lines.map((line, index) => {
      const trimmedLine = line.trim();

      if (
        trimmedLine.includes('MAONI YA RECTOR') ||
        trimmedLine.includes('TABIA YA MSEMINARI') ||
        trimmedLine.includes('1. Wito Kwa Wazazi na Walezi') ||
        trimmedLine.includes('2. Wito Kwa Waseminari') ||
        trimmedLine.includes('3. Tarehe ya Kikao kwa Wazazi') ||
        trimmedLine.includes('4. Tarehe ya Kufungua Seminari') ||
        trimmedLine.includes('Fr Philip Tairo') ||
        trimmedLine.includes('Rector')
      ) {
        return (
          <Typography
            key={index}
            variant="body1"
            sx={{
              textAlign: "justify",
              fontSize: "1rem",
              lineHeight: 1.8,
              fontFamily: "serif",
              fontWeight: "bold",
              mb: 1,
            }}
          >
            {trimmedLine}
          </Typography>
        );
      }

      // Check for bullet points
      if (trimmedLine.startsWith('•')) {
        return (
          <Box key={index} sx={{ display: "flex", alignItems: "flex-start", mb: 0.5, ml: 2 }}>
            <Typography sx={{ mr: 1, fontSize: "1rem" }}>•</Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: "justify",
                fontSize: "1rem",
                lineHeight: 1.8,
                fontFamily: "serif",
                flex: 1,
              }}
            >
              {trimmedLine.substring(1).trim()}
            </Typography>
          </Box>
        );
      }

      // Regular text
      return (
        <Typography
          key={index}
          variant="body1"
          sx={{
            textAlign: "justify",
            fontSize: "1rem",
            lineHeight: 1.8,
            fontFamily: "serif",
            mb: 1,
          }}
        >
          {trimmedLine}
        </Typography>
      );
    });
  };

  const handlePrint = () => {
    const reportPayload = buildReportForPdf();
    if (!reportPayload) return;
    const blob = generateStudentEvaluationReport(reportPayload, { returnBlob: true });
    if (!blob || !(blob instanceof Blob)) return;
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (!printWindow) {
      URL.revokeObjectURL(url);
      return;
    }
    printWindow.onload = () => {
      try {
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          URL.revokeObjectURL(url);
        }, 250);
      } catch {
        URL.revokeObjectURL(url);
      }
    };
    printWindow.onerror = () => URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);

        // Load evaluation report
        const evaluationData = await getEvaluationByTermIdAndStudentId(termId, studentId);
        setReport(evaluationData);

        // Load Rector comment details
        try {
          const rectorCommentData = await getRectorCommentByTermStudent(termId, studentId);
          setRectorComment(rectorCommentData);
        } catch (error) {
          // Rector comment might not exist, which is okay
          if (error.response?.status !== 404) {
            console.error("Error loading rector comment:", error);
          }
          setRectorComment(null);
        }

        // Load Accountant details
        try {
          const accountantDetails = await getAccountantByTermStudent(termId, studentId);
          setAccountantData(accountantDetails);
        } catch (error) {
          // Accountant data might not exist, which is okay
          if (error.response?.status !== 404) {
            console.error("Error loading accountant details:", error);
          }
          setAccountantData(null);
        }
      } catch (error) {
        console.error("Error loading student evaluation report:", error);
      } finally {
        setLoading(false);
      }
    };

    if (termId && studentId) {
      loadReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termId, studentId]);

  const sections = [
    {
      title: "MAISHA YA KIROHO",
      field: "spiritualLife",
      items: [
        "Ushiriki wake katika Misa, Ibada mbalimbali, na mazoezi ya kiroho.",
        "Ushiriki katika vikundi mbalimbali vya sala.",
        "Kuongoza sala, kuimba, kushiriki Misa na kusoma Neno la Mungu.",
        "Kuwa na nidhamu na heshima kanisani.",
      ],
    },
    {
      title: "MAISHA YA KITAALUMA",
      field: "academicLife",
      items: [
        "Kuhudhuria darasani na kutumia muda vizuri.",
        "Kufanya mazoezi na majaribio kwa wakati.",
        "Utulivu, umakini, usikivu na ushiriki darasani.",
        "Bidii ya kuzungumza Kiingereza.",
      ],
    },
    {
      title: "KAZI ZA MIKONO",
      field: "manualWork",
      items: [
        "Kufika kazini kwa wakati akiwa na vifaa sahihi.",
        "Bidii na juhudi katika kazi.",
        "Kufanya kazi nzuri.",
        "Moyo wa kujitolea katika kazi.",
        "Utiifu na kufanya kazi kwa wakati.",
      ],
    },
    {
      title: "UTU",
      field: "behaviour",
      items: [
        "Uaminifu",
        "Utunzaji wa mali yake na isiyo yake",
        "Heshima na ushirikiano kwa walezi na waseminari wenzake",
        "Utii kwa maagizo anayopewa",
        "Kukubali kosa na kuomba radhi",
        "Kuzingatia na kufuata ratiba ya seminari vizuri ",
        "Kuzingatia uvaaji wa sare rasmi ya seminari",
      ],
    },
    {
      title: "AFYA",
      field: "health",
      items: [
        "Kuzingatia usafi wa mwili, mavazi na kupasi nguo.",
        "Uwepesi wa kwenda zahanati anapoumwa.",
        "Kutumia dawa kwa wakati na kwa ukamilifu.",
      ],
    },
    {
      title: "UONGOZI",
      field: "leadershipSkills",
      items: [
        "Kipaji cha kuwa kiongozi bora.",
        "Kushawishi wenzake kufuata sheria.",
        "Kuheshimu na kushirikiana na viongozi.",
      ],
    },
    {
      title: "MICHEZO",
      field: "sports",
      items: [
        "Kupenda kushiriki michezo (mpira, mazoezi, kukimbia).",
        "Kushiriki sanaa mbalimbali.",
        "Kutunza vifaa vya michezo.",
      ],
    },
  ];

  // Financial data configuration - prioritize accountant data if available, otherwise use report data
  const financialData = [
    {
      description: "Deni",
      field: "debts",
      amount: accountantData?.firstTermDebt || accountantData?.secondTermDebt || report?.debts || 0,
    },
    {
      description: "Ada ya Muhula wa Kwanza",
      field: "firstTermFee",
      amount: accountantData?.firstTermFee || report?.firstTermFee || 0,
    },
    {
      description: "Ada ya Muhula wa Pili",
      field: "secondTermFee",
      amount: accountantData?.secondTermFee || report?.secondTermFee || 0,
    },
    {
      description: "Mchango wa Mitihani ya muhula wa Kwanza",
      field: "firstTermExamFee",
      amount: accountantData?.firstTermExamFee || report?.firstTermExamFee || 0,
    },
    {
      description: "Mchango  wa Mitihani ya muhula Pili",
      field: "secondTermExamFee",
      amount: accountantData?.secondTermExamFee || report?.secondTermExamFee || 0,
    },
    {
      description: "Michango mingine ya Muhula wa Kwanza",
      field: "firstTermOtherContribution",
      amount: accountantData?.firstTermOtherContribution || report?.firstTermOtherContribution || 0,
    },
    {
      description: "Michango Mingine ya Muhula wa Pili",
      field: "secondTermOtherContribution",
      amount: accountantData?.secondTermOtherContribution || report?.secondTermOtherContribution || 0,
    },
    // Additional accountant-specific fields
    ...(accountantData?.firstTermParentFee ? [{
      description: "Ada ya Wazazi ya Muhula wa Kwanza",
      field: "firstTermParentFee",
      amount: accountantData.firstTermParentFee || 0,
    }] : []),
    ...(accountantData?.secondTermParentFee ? [{
      description: "Ada ya Wazazi ya Muhula wa Pili",
      field: "secondTermParentFee",
      amount: accountantData.secondTermParentFee || 0,
    }] : []),
    ...(accountantData?.firstTermPaidAmount ? [{
      description: "Kiasi Kilicholipwa - Muhula wa Kwanza",
      field: "firstTermPaidAmount",
      amount: accountantData.firstTermPaidAmount || 0,
    }] : []),
    ...(accountantData?.secondTermPaidAmount ? [{
      description: "Kiasi Kilicholipwa - Muhula wa Pili",
      field: "secondTermPaidAmount",
      amount: accountantData.secondTermPaidAmount || 0,
    }] : []),
  ];

  // Calculate totals
  const totalAmount = financialData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const hasFinancialData = financialData.some(item => parseFloat(item.amount) > 0);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-TZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount) || 0);
  };

  return (
    <Box
      className="printable-content"
      sx={{
        ml: "20px",
        mr: "20px",
        "@media print": {
          ml: 0,
          mr: 0,
          width: "100%",
          maxWidth: "none",
          padding: "10px",
          backgroundColor: "white",
          color: "black",
          fontSize: "10px",
          lineHeight: 1.2,
          "& *": {
            color: "black !important",
            backgroundColor: "transparent !important",
            boxShadow: "none !important",
          },
          "& .MuiBox-root": {
            pageBreakInside: "avoid",
            marginBottom: "8px",
          },
          "& .MuiTypography-root": {
            color: "black !important",
          },
          "& .MuiTable-root": {
            borderCollapse: "collapse",
            width: "100%",
            fontSize: "9px",
          },
          "& .MuiTableCell-root": {
            border: "1px solid black !important",
            padding: "4px",
            background: "white !important",
            color: "black !important",
            fontSize: "9px",
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            background: "#f0f0f0 !important",
            fontWeight: "bold",
            fontSize: "9px",
          },
          "& .MuiCard-root": {
            border: "1px solid black !important",
            background: "white !important",
            marginBottom: "8px",
            padding: "8px",
          },
          "& .MuiButton-root": {
            display: "none !important",
          },
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            fontSize: "12px !important",
            margin: "5px 0 !important",
          },
          "& .MuiTypography-h4": {
            fontSize: "11px !important",
            margin: "5px 0 !important",
          },
          "& .MuiTypography-h6": {
            fontSize: "10px !important",
            margin: "3px 0 !important",
          },
          "& .MuiTypography-body1": {
            fontSize: "9px !important",
            lineHeight: "1.2 !important",
          },
          "& .MuiTypography-body2": {
            fontSize: "8px !important",
            lineHeight: "1.2 !important",
          },
        },
      }}
    >
      {/* Print and Download Buttons */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          "@media print": { display: "none" },
        }}
      >
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{
            backgroundColor: colors.blueAccent[500],
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
          }}
        >
          Print Report
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            const reportPayload = buildReportForPdf();
            if (reportPayload) generateStudentEvaluationReport(reportPayload);
          }}
          sx={{
            backgroundColor: "#502ecc",
            "&:hover": {
              backgroundColor: "#3d2399",
            },
          }}
        >
          📄 Download PDF
        </Button>
      </Box>

      {/* Header */}
      <Box
        height="auto"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        border={`2px solid ${borderColor}`}
        mb={3}
        flexWrap="wrap"
        sx={{
          "@media print": {
            border: "1px solid black",
            pageBreakInside: "avoid",
            marginBottom: "10px",
            padding: "5px",
            backgroundColor: "white",
            "& *": {
              color: "black !important",
              backgroundColor: "transparent !important",
            },
            "& .MuiTypography-root": {
              fontSize: "8px !important",
              lineHeight: "1.1 !important",
            },
            "& img": {
              width: "40px !important",
              height: "40px !important",
            },
          },
        }}
      >
        {/* Left Section */}
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          borderRight={{ xs: "none", md: `1px solid ${borderColor}` }}
          p={1}
          overflow="hidden"
          justifyContent="center"
        >
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            alignItems="center"
            textAlign={{ xs: "center", md: "left" }}
          >
            <Box
              component="img"
              src={image.visigalogo}
              alt="logo"
              sx={{
                height: 80,
                width: 80,
                mb: { xs: 1, md: 0 },
                mr: { xs: 0, md: 2 },
              }}
            />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: "0.7rem", sm: "1.1rem", md: "1.5rem" } }}
            >
              St Mary's Junior Seminary (VISIGA SEMINARY)
            </Typography>
          </Box>
        </Box>

        {/* Center Section */}
        <Box
          flex={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
          borderRight={{ xs: "none", md: `1px solid ${borderColor}` }}
          p={2}
          textAlign="center"
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.4rem" } }}
          >
            TATHMINI YA MWENDENDO WA MSEMINARI
          </Typography>
        </Box>

        {/* Right Section */}
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          p={1}
          overflow="hidden"
          justifyContent="center"
        >
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            alignItems="center"
            textAlign={{ xs: "center", md: "left" }}
          >
            <Box
              component="img"
              src={image.cathedral5}
              alt="arch"
              sx={{
                height: 80,
                width: 80,
                mb: { xs: 1, md: 0 },
                mr: { xs: 0, md: 2 },
              }}
            />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: "0.7rem", sm: "1.1rem", md: "1.5rem" } }}
            >
              Seminary under Archdiocese of Dar es Salaam
            </Typography>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <LoadingSpinner message="Loading report..." />
      ) : report ? (
        <>
          {/* Student Profile Section */}
          <Box
            display="flex"
            flexDirection="column"
            mb={3}
            sx={{
              "@media print": {
                marginBottom: "8px",
                "& .MuiTypography-root": {
                  fontSize: "9px !important",
                  lineHeight: "1.2 !important",
                },
              },
            }}
          >
            <Typography variant="body1">
              <Box
                component="span"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  "@media print": {
                    fontSize: "9px !important",
                  }
                }}
              >
                <strong>JINA LA MSEMINARI:</strong>{" "}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  "@media print": {
                    fontSize: "9px !important",
                  }
                }}
              >
                {report.studentName || "NOT ASSIGNED"}
              </Box>
            </Typography>

            <Typography variant="body1" sx={{ mt: 1 }}>
              <Box
                component="span"
                sx={{
                  fontSize: "1.2rem",
                  "@media print": {
                    fontSize: "9px !important",
                  }
                }}
              >
                <strong>TERM:</strong> {report.termName || "N/A"}
              </Box>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h4" align="left">
              STUDENT EVALUATION REPORT
            </Typography>
          </Box>

          {/* Rector's Comments */}
          <Card
            sx={{
              p: 3,
              mb: 4,
              mt: 3,
              backgroundColor: colors.primary[400],
              border: `1px solid ${borderColor}`,
              "@media print": {
                backgroundColor: "white",
                border: "1px solid black",
                pageBreakInside: "avoid",
              },
            }}
          >
            <Box sx={{ pl: { xs: 2, md: 4 }, pr: { xs: 2, md: 4 } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  color: colors.greenAccent[600],
                  "@media print": {
                    color: "black",
                    fontSize: "10px !important",
                  },
                }}
              >
                MAONI YA RECTOR
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  mb: 1,
                  fontFamily: "serif",
                  "@media print": { fontSize: "9px !important" },
                }}
              >
                1. Wito Kwa Wazazi na Walezi
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textAlign: "justify",
                  fontSize: "1rem",
                  lineHeight: 1.8,
                  fontFamily: "serif",
                  mb: 2,
                  "@media print": { fontSize: "9px !important" },
                }}
              >
                Kumuelekeza katika uwajibikaji wa nyumbani na kumlea katika maadili mema, kumsaidia kudumisha sala, unyenyekevu, kazi na nidhamu, tukikumbuka kuwa maisha ya seminari yanaimarika pale ambapo malezi ya nyumbani yanajengwa kwa uzito na umakini.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  mb: 1,
                  fontFamily: "serif",
                  "@media print": { fontSize: "9px !important" },
                }}
              >
                2. Wito Kwa Waseminari
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textAlign: "justify",
                  fontSize: "1rem",
                  lineHeight: 1.8,
                  fontFamily: "serif",
                  mb: 2,
                  "@media print": { fontSize: "9px !important" },
                }}
              >
                Likizo si mapumziko kutoka maisha ya sala, nawasihi kwa dhati kushiriki jumuiya, misa za asubuhi, Dominika na shughuli mbalimbali parokiani, pamoja na kutunza adabu, nidhamu na utulivu popote mlipo.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  mb: 1,
                  fontFamily: "serif",
                  "@media print": { fontSize: "9px !important" },
                }}
              >
                TABIA YA MSEMINARI:
              </Typography>

              {getSavedRectorComments() ? (
                formatRectorComments(getSavedRectorComments())
              ) : (
                <Box sx={{ mt: 1 }}>
                  {[...Array(5)].map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: borderColor,
                        height: 28,
                        mb: 0.5,
                      }}
                    />
                  ))}
                </Box>
              )}

              <Box sx={{ mt: 3, textAlign: "right" }}>
                <Box sx={{ height: 24 }} />
                <Typography sx={{ fontWeight: "bold" }}>Rector</Typography>
                <Typography sx={{ fontSize: "0.9rem", mt: 0.5 }}>
                  St Mary's Junior Seminary
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Evaluation Sections */}
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            }}
            gridAutoRows="auto"
            gap="20px"
            mt={2}
            sx={{
              "@media print": {
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "15px",
                pageBreakInside: "avoid",
                "& .MuiBox-root": {
                  backgroundColor: "white !important",
                  border: "1px solid black !important",
                  padding: "10px",
                  marginBottom: "10px",
                  "& *": {
                    color: "black !important",
                    backgroundColor: "transparent !important",
                  },
                },
              },
            }}
          >
            {sections.map((section, index) => (
              <Box
                key={index}
                backgroundColor={colors.primary[400]}
                display="flex"
                flexDirection="column"
                p={2}
                borderRadius={1}
                border={`1px solid ${borderColor}`}
                sx={{
                  "@media print": {
                    backgroundColor: "white",
                    border: "1px solid black",
                    pageBreakInside: "avoid",
                    marginBottom: "10px",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    mb: 1,
                    color: colors.greenAccent[600],
                    fontSize: "1rem",
                    textAlign: "center",
                  }}
                >
                  {section.title}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box display="flex" flexDirection="column" gap={1} flex={1}>
                  {section.items.map((item, idx) => (
                    <Typography
                      key={idx}
                      sx={{
                        fontSize: "0.85rem",
                        lineHeight: 1.3,
                      }}
                    >
                      • {item}
                    </Typography>
                  ))}
                </Box>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Typography
                    sx={{
                      width: 50,
                      height: 50,
                      textAlign: "center",
                      border: `2px solid ${borderColor}`,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                      backgroundColor:
                        report[section.field] === "A"
                          ? colors.greenAccent[500]
                          : report[section.field] === "B"
                            ? colors.blueAccent[500]
                            : report[section.field] === "F"
                              ? colors.redAccent[500]
                              : colors.grey[500],
                      color: "white",
                    }}
                  >
                    {report[section.field] || "?"}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Enhanced Financial Information Section */}
          {hasFinancialData && (
            <Box mt={4}>
              <Typography
                variant="h4"
                align="left"
                sx={{
                  mb: 3,
                  color: colors.greenAccent[600],
                  "@media print": {
                    color: 'black',
                    fontWeight: 'bold'
                  }
                }}
              >
                TAARIFA ZA KIFEDHA
              </Typography>

              <TableContainer
                component={Paper}
                sx={{
                  backgroundColor: colors.primary[400],
                  border: `1px solid ${borderColor}`,
                  mb: 3,
                  "@media print": {
                    backgroundColor: "white",
                    border: "1px solid black",
                  },
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: colors.primary[400],
                          color: colors.greenAccent[600],
                          fontSize: '1rem',
                          border: `1px solid ${borderColor}`,
                          "@media print": {
                            backgroundColor: 'white',
                            color: 'black',
                            border: '1px solid black',
                          }
                        }}
                      >
                        Maelezo
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: colors.primary[400],
                          color: colors.greenAccent[600],
                          fontSize: '1rem',
                          border: `1px solid ${borderColor}`,
                          "@media print": {
                            backgroundColor: 'white',
                            color: 'black',
                            border: '1px solid black',
                          }
                        }}
                      >
                        Kiasi (TZS)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {financialData.map((item, index) => (
                      parseFloat(item.amount) > 0 && (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor: colors.primary[400],
                            border: `1px solid ${borderColor}`,
                            "@media print": {
                              backgroundColor: 'white',
                              border: '1px solid black',
                            }
                          }}
                        >
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{
                              fontWeight: 'medium',
                              backgroundColor: colors.primary[400],
                              color: colors.greenAccent[600],
                              border: `1px solid ${borderColor}`,
                              "@media print": {
                                backgroundColor: 'white',
                                border: '1px solid black',
                                color: 'black'
                              }
                            }}
                          >
                            {item.description}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 'bold',
                              backgroundColor: colors.primary[400],
                              color: colors.greenAccent[600],
                              border: `1px solid ${borderColor}`,
                              "@media print": {
                                backgroundColor: 'white',
                                border: '1px solid black',
                                color: 'black'
                              }
                            }}
                          >
                            {formatCurrency(item.amount)}
                          </TableCell>
                        </TableRow>
                      )
                    ))}
                    {/* Total Row */}
                    <TableRow
                      sx={{
                        backgroundColor: colors.primary[400],
                        border: `2px solid ${borderColor}`,
                        '& td': {
                          fontWeight: 'bold',
                          backgroundColor: colors.primary[400],
                          color: colors.greenAccent[600],
                          fontSize: '1.1rem',
                          border: `1px solid ${borderColor}`,
                          "@media print": {
                            backgroundColor: 'white',
                            color: 'black',
                            border: '1px solid black',
                          }
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 'bold' }}>JUMLA</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(totalAmount)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Additional Financial Notes */}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: colors.primary[400],
                  border: `1px solid ${borderColor}`,
                  borderRadius: 1,
                  "@media print": {
                    backgroundColor: 'white',
                    border: "1px solid black",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1,
                    color: colors.greenAccent[600],
                    "@media print": {
                      color: 'black',
                      fontWeight: 'bold'
                    }
                  }}
                >
                  Maelekezo ya Malipo:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontStyle: 'italic',
                    "@media print": {
                      color: 'black'
                    }
                  }}
                >
                  • Malipo yote yafanyike kwenye akaunti ya Seminari<br />
                  • Hakikisha unapokea risiti ya malipo<br />
                  • Wasiliana na ofisi ya fedha kwa maelekezo zaidi<br />
                  • Malipo ya ada ya muhula yafanyike kabla ya kuanza kwa muhula
                </Typography>
              </Box>
            </Box>
          )}

          {/* Legend */}
          <Box mt={3}>
            <Typography variant="h4" align="left" sx={{ mb: 2 }}>
              MAANA YA MADARAJA
            </Typography>
            <Box
              backgroundColor={colors.primary[400]}
              p={3}
              borderRadius={1}
              border={`1px solid ${borderColor}`}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 2, color: colors.greenAccent[600] }}
              >
                MAANA YA MADARAJA:
              </Typography>
              <Box display="flex" gap={3} mb={2} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      backgroundColor: colors.greenAccent[500],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    A
                  </Box>
                  <Typography>= Vizuri</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      backgroundColor: colors.blueAccent[500],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    B
                  </Box>
                  <Typography>= Wastani</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      backgroundColor: colors.redAccent[500],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    F
                  </Box>
                  <Typography>= Dhaifu</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: "0.9rem", fontStyle: "italic" }}>
                Kwa mujibu wa sheria na taratibu za Seminari, kupata "F" kwenye
                kipengele cha Maisha ya Kiroho au Tabia kunaweza kuashiria
                changamoto kubwa kwa malezi.
              </Typography>
            </Box>
          </Box>
        </>

      ) : (
        <Typography textAlign="center" mt={4} color="text.secondary">
          Hakuna taarifa za mwendendo zilizopatikana.
        </Typography>
      )}
    </Box>
  );
};

export default StudentEvaluationReport;